import React, { useEffect, useRef, useState } from "react";
import SmallLoader from "../../../Hooks/SmallLoader";
import "./exportMenu.css";

/**
 * Three-dots button that opens a small menu of export actions.
 *
 * items: [{ label, icon, onClick, loading?, disabled? }]
 *   icon is a Font Awesome class, e.g. "fa-solid fa-file-excel".
 */
const ExportMenu = ({ items = [], label = "Export options" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const handleItemClick = (item) => {
    setIsOpen(false);
    item.onClick?.();
  };

  const isBusy = items.some((item) => item.loading);

  return (
    <div className="export-menu" ref={wrapperRef}>
      <button
        type="button"
        className={`export-menu-trigger${isOpen ? " is-open" : ""}`}
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={label}
        title={label}
      >
        {isBusy ? <SmallLoader /> : <i className="fa-solid fa-ellipsis-vertical" />}
      </button>

      {isOpen && (
        <div className="export-menu-list" role="menu">
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className="export-menu-item"
              onClick={() => handleItemClick(item)}
              disabled={item.disabled || item.loading}
            >
              <i className={`${item.icon} export-menu-icon`} />
              <span>{item.label}</span>
              {item.loading && <span className="export-menu-loading">…</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExportMenu;
